import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  ShoppingCart, Plus, Minus, Trash2, Calendar, Clock, MapPin, User, Mail, Phone, 
  CreditCard, Lock, Loader2, CheckCircle, AlertCircle, Package, ChevronDown, ChevronUp,
  FileText, X, Search, ArrowLeft, RotateCcw, ImageIcon, DollarSign, ShieldCheck
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/AddressAutocomplete';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Styling for individual Stripe elements to match dark theme
const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#64748b',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
    complete: {
      color: '#22c55e',
      iconColor: '#22c55e',
    },
  },
};

// Stripe Submit Button Component using proper Elements API with separated fields
const RentalStripeSubmitButton = ({ onApprove, onError, disabled, total, customerEmail, customerName, description, postalCode }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const handleClick = async () => {
    if (!stripe || !elements) {
      onError(new Error("Payment system not ready. Please refresh and try again."));
      return;
    }
    
    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      onError(new Error("Card details not ready. Please try again."));
      return;
    }
    
    // Validate amount before proceeding
    if (!total || total <= 0) {
      onError(new Error("Invalid payment amount. Please refresh and try again."));
      return;
    }
    
    // Round to 2 decimal places to avoid floating point issues
    const chargeAmount = Math.round(total * 100) / 100;
    
    // Log the exact amount being sent to Stripe
    console.log('💳 Rental Payment - Sending to Stripe:', {
      originalTotal: total,
      chargeAmount: chargeAmount,
      chargeAmountFixed: chargeAmount.toFixed(2),
      description: description,
      customerEmail: customerEmail,
      customerName: customerName
    });
    
    setPaying(true);
    try {
      // Create PaymentIntent on backend with the validated amount
      const intentResponse = await axios.post(`${API}/payments/create-intent`, {
        amount: chargeAmount,
        description: description || 'Equipment Rental',
        customer_email: customerEmail,
        customer_name: customerName,
      });
      
      console.log('💳 PaymentIntent created:', intentResponse.data);

      if (!intentResponse.data.success) {
        throw new Error(intentResponse.data.error || 'Failed to create payment');
      }

      const { client_secret, payment_intent_id } = intentResponse.data;

      // Confirm the payment with Stripe using CardNumberElement
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: customerName || undefined,
            email: customerEmail || undefined,
            address: {
              postal_code: postalCode || undefined,
            },
          },
        },
      });

      if (error) {
        console.error("Payment error:", error);
        let errorMessage = error.message || "Payment failed. Please try again.";
        if (error.code === 'card_declined') {
          errorMessage = 'Your card was declined. Please try a different payment method.';
        } else if (error.code === 'expired_card') {
          errorMessage = 'Your card has expired. Please use a different card.';
        } else if (error.code === 'insufficient_funds') {
          errorMessage = 'Insufficient funds. Please try a different payment method.';
        }
        onError(new Error(errorMessage));
      } else if (paymentIntent.status === 'succeeded') {
        await onApprove({ 
          paymentIntentId: paymentIntent.id,
          status: paymentIntent.status 
        });
      }
    } catch (error) {
      console.error("Payment error:", error);
      onError(new Error(error.message || "Payment failed. Please try again."));
    } finally {
      setPaying(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled || paying || total <= 0 || !stripe}
      className="w-full h-14 bg-purple-600 hover:bg-purple-500 text-white font-semibold text-lg rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid="rental-pay-button"
    >
      {paying ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          <Lock className="h-5 w-5" />
          Pay ${total.toFixed(2)}
        </>
      )}
    </Button>
  );
};

// Calculate rental days based on 24-hour increments
const calculateRentalDays = (pickupDate, pickupTime, returnDate, returnTime) => {
  if (!pickupDate || !pickupTime || !returnDate || !returnTime) return 1;
  
  const pickup = new Date(`${pickupDate}T${pickupTime}`);
  const returnDt = new Date(`${returnDate}T${returnTime}`);
  
  const diffMs = returnDt - pickup;
  const diffHours = diffMs / (1000 * 60 * 60);
  
  if (diffHours <= 24) return 1;
  return Math.ceil(diffHours / 24);
};

const EquipmentRentals = () => {
  // Products and settings
  const [products, setProducts] = useState({ rentals: [], ecards: [] });
  const [settings, setSettings] = useState(null);
  const [eligibleInstructors, setEligibleInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Product images loaded on demand (to avoid 80MB+ responses)
  const [productImages, setProductImages] = useState({}); // { sku: image_url }
  const [loadingImages, setLoadingImages] = useState({}); // { sku: true/false }
  
  // Inventory availability tracking
  const [inventoryAvailability, setInventoryAvailability] = useState({}); // { sku: { available_qty, is_configured, unlimited } }
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  
  // Cart
  const [cart, setCart] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({ rentals: true, ecards: false });
  
  // Customer info
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [instructorSearch, setInstructorSearch] = useState('');
  
  // Schedule
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  
  // Auto-set return time when pickup time changes
  const handlePickupTimeChange = (newTime) => {
    setPickupTime(newTime);
    // Auto-set return time to match pickup time
    if (newTime && !returnTime) {
      setReturnTime(newTime);
    }
  };
  
  // Check if schedule is complete (required before adding items)
  const isScheduleComplete = pickupDate && pickupTime && returnDate && returnTime;
  
  // Location
  const [pickupLocationType, setPickupLocationType] = useState('predefined');
  const [pickupLocationId, setPickupLocationId] = useState('');
  const [pickupCustomAddress, setPickupCustomAddress] = useState('');
  const [isCustomAddressValid, setIsCustomAddressValid] = useState(false);
  const [customAddressData, setCustomAddressData] = useState(null); // Full address data from Google
  // Dropoff is always same as pickup - these are kept for API compatibility
  
  // Terms and checkout
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  
  // Image lightbox
  const [lightboxImage, setLightboxImage] = useState(null); // { url, name }
  
  // Replacement costs modal
  const [showReplacementCosts, setShowReplacementCosts] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Payment
  const [showCheckout, setShowCheckout] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState(null);
  const [rentalPostalCode, setRentalPostalCode] = useState('');
  const [rentalCardFieldStatus, setRentalCardFieldStatus] = useState({
    cardNumber: { complete: false, error: null },
    cardExpiry: { complete: false, error: null },
    cardCvc: { complete: false, error: null },
  });
  
  // Calendar availability for pickup/dropoff times
  const [calendarAvailability, setCalendarAvailability] = useState({ pickup: null, dropoff: null });
  const [checkingCalendar, setCheckingCalendar] = useState(false);
  
  // Coupon/Discount
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null); // null = not applied, object = applied coupon data
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsRes, settingsRes, instructorsRes] = await Promise.all([
          axios.get(`${API}/rentals/products`),
          axios.get(`${API}/rentals/settings`),
          axios.get(`${API}/rentals/eligible-instructors`)
        ]);
        
        setProducts(productsRes.data);
        setSettings(settingsRes.data);
        setEligibleInstructors(instructorsRes.data.instructors || []);
        
        // Set default pickup location
        if (settingsRes.data.pickup_locations?.length > 0) {
          setPickupLocationId(settingsRes.data.pickup_locations[0].id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load rental data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Function to load product image on demand
  const loadProductImage = async (sku) => {
    if (productImages[sku] || loadingImages[sku]) {
      return; // Already loaded or loading
    }
    
    setLoadingImages(prev => ({ ...prev, [sku]: true }));
    try {
      const response = await axios.get(`${API}/rentals/products/${encodeURIComponent(sku)}/image`);
      if (response.data?.image_url) {
        setProductImages(prev => ({ ...prev, [sku]: response.data.image_url }));
      }
    } catch (error) {
      console.error(`Error loading image for ${sku}:`, error);
    } finally {
      setLoadingImages(prev => ({ ...prev, [sku]: false }));
    }
  };
  
  // Load images for products that have them when products list is populated
  useEffect(() => {
    if (products.rentals.length > 0) {
      products.rentals.forEach(product => {
        if (product.has_image && !productImages[product.sku]) {
          loadProductImage(product.sku);
        }
      });
    }
  }, [products.rentals]);
  
  // Fetch inventory availability when dates change
  useEffect(() => {
    const fetchAvailability = async () => {
      // Need all 4 values (dates AND times) for valid datetime
      if (!pickupDate || !pickupTime || !returnDate || !returnTime || products.rentals.length === 0) {
        return;
      }
      
      setLoadingAvailability(true);
      try {
        // Check availability for all rental products
        const items = products.rentals.map(p => ({
          sku: p.sku,
          name: p.name,
          quantity: 1,
          type: 'rental'
        }));
        
        const pickupDt = `${pickupDate}T${pickupTime}`;
        const returnDt = `${returnDate}T${returnTime}`;
        
        const response = await axios.post(
          `${API}/rentals/inventory/check-availability?pickup_datetime=${encodeURIComponent(pickupDt)}&return_datetime=${encodeURIComponent(returnDt)}`,
          items
        );
        
        // Build availability map
        const availMap = {};
        for (const item of response.data.items) {
          availMap[item.sku] = {
            available_qty: item.available_qty ?? null,
            is_configured: item.is_configured ?? false,
            unlimited: item.unlimited ?? false,
            total: item.total ?? 0,
            reserved: item.reserved ?? 0
          };
        }
        setInventoryAvailability(availMap);
      } catch (error) {
        console.error('Error fetching availability:', error);
        // On error, don't block - treat as unlimited
        setInventoryAvailability({});
      } finally {
        setLoadingAvailability(false);
      }
    };
    
    fetchAvailability();
  }, [pickupDate, pickupTime, returnDate, returnTime, products.rentals]);
  
  // Check calendar availability when pickup/return times change
  useEffect(() => {
    const checkCalendarAvailability = async () => {
      // Need date and time for both pickup and return
      if (!pickupDate || !pickupTime) {
        setCalendarAvailability({ pickup: null, dropoff: null });
        return;
      }
      
      setCheckingCalendar(true);
      try {
        const requestBody = {
          pickup_datetime: `${pickupDate}T${pickupTime}`,
        };
        
        // Add return datetime if both fields are filled
        if (returnDate && returnTime) {
          requestBody.return_datetime = `${returnDate}T${returnTime}`;
        }
        
        const response = await axios.post(`${API}/rentals/check-calendar-availability`, requestBody);
        
        setCalendarAvailability({
          pickup: response.data.pickup,
          dropoff: response.data.dropoff
        });
      } catch (error) {
        console.error('Error checking calendar availability:', error);
        // On error, don't block the user - treat as available
        setCalendarAvailability({ pickup: null, dropoff: null });
      } finally {
        setCheckingCalendar(false);
      }
    };
    
    checkCalendarAvailability();
  }, [pickupDate, pickupTime, returnDate, returnTime]);
  
  // Fetch Stripe config when checkout starts
  const fetchStripeToken = async () => {
    try {
      const response = await axios.get(`${API}/stripe/config`);
      if (response.data?.publishable_key) {
        setStripePublishableKey(response.data.publishable_key);
        const stripe = loadStripe(response.data.publishable_key);
        setStripePromise(stripe);
        return true;
      }
      throw new Error('No publishable key in response');
    } catch (error) {
      console.error('Error fetching Stripe config:', error);
      toast.error('Failed to initialize payment system. Please try again.');
      return false;
    }
  };
  
  // Apply coupon function
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    if (cart.length === 0) {
      setCouponError('Please add items to your cart first');
      return;
    }
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      // Build items list for validation
      const items = cart.map(item => ({
        sku: item.sku,
        name: item.name,
        quantity: item.quantity,
        unit_price: item.price * ((item.type === 'rental' && !item.flat_rate) ? rentalDays : 1),
        type: item.type
      }));
      
      const response = await axios.post(`${API}/rentals/validate-coupon`, {
        coupon_code: couponCode.trim(),
        items: items,
        customer_email: selectedInstructor?.email || null
      });
      
      if (response.data.valid) {
        setCouponApplied(response.data);
        setCouponError('');
        toast.success(`Coupon applied! You save $${response.data.discount_amount.toFixed(2)}`);
      } else {
        setCouponError(response.data.error || 'Invalid coupon code');
        setCouponApplied(null);
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError(error.response?.data?.detail || 'Failed to validate coupon');
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };
  
  // Remove coupon function
  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setCouponError('');
    toast.info('Coupon removed');
  };
  
  // Clear coupon when cart changes significantly
  useEffect(() => {
    if (couponApplied && cart.length === 0) {
      setCouponApplied(null);
      setCouponCode('');
    }
  }, [cart, couponApplied]);
  
  // Calculate rental days
  const rentalDays = useMemo(() => {
    return calculateRentalDays(pickupDate, pickupTime, returnDate, returnTime);
  }, [pickupDate, pickupTime, returnDate, returnTime]);
  
  // Calculate totals
  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      // For flat_rate items, charge once regardless of days
      // For regular rental items, multiply by number of days
      // For eCards, always charge once
      const days = (item.type === 'rental' && !item.flat_rate) ? rentalDays : 1;
      return sum + (item.price * item.quantity * days);
    }, 0);
  }, [cart, rentalDays]);
  
  const depositAmount = settings?.deposit_amount || 50;
  
  // Calculate delivery fee based on selected pickup/return location
  // If custom location, use default delivery fee; otherwise use location-specific fee
  const deliveryFee = useMemo(() => {
    if (pickupLocationType === 'custom') {
      return settings?.delivery_fee || 0;
    }
    if (pickupLocationId) {
      const selectedLocation = settings?.pickup_locations?.find(l => l.id === pickupLocationId);
      if (selectedLocation && selectedLocation.delivery_fee !== undefined) {
        return selectedLocation.delivery_fee;
      }
    }
    return settings?.delivery_fee || 0;
  }, [pickupLocationType, pickupLocationId, settings]);
  
  // Calculate discount amount from applied coupon
  const discountAmount = couponApplied?.discount_amount || 0;
  
  // Total = subtotal + deposit + delivery - discount
  const total = Math.max(0, subtotal + depositAmount + deliveryFee - discountAmount);
  
  // Get max quantity available for a product
  const getMaxAvailable = (product) => {
    // eCards have no inventory limits in this system
    if (product.type !== 'rental') return null; // null = unlimited
    
    const avail = inventoryAvailability[product.sku];
    if (!avail || !avail.is_configured || avail.unlimited) {
      return null; // unlimited
    }
    
    // Get current quantity in cart
    const cartItem = cart.find(item => item.sku === product.sku);
    const inCart = cartItem ? cartItem.quantity : 0;
    
    // Max they can add is available_qty (already accounts for other reservations)
    // But we need to also consider what's already in their cart
    return avail.available_qty;
  };
  
  // Check if a product can be added to cart
  const canAddToCart = (product) => {
    const maxAvail = getMaxAvailable(product);
    if (maxAvail === null) return true; // unlimited
    
    // Check current cart quantity
    const cartItem = cart.find(item => item.sku === product.sku);
    const inCart = cartItem ? cartItem.quantity : 0;
    
    return inCart < maxAvail;
  };
  
  // Get availability display text for a product
  const getAvailabilityText = (product) => {
    if (product.type !== 'rental') return null;
    if (!pickupDate || !returnDate) return null;
    
    const avail = inventoryAvailability[product.sku];
    if (!avail) return null;
    
    if (!avail.is_configured || avail.unlimited) return null; // unlimited, no message needed
    
    if (avail.available_qty === 0) {
      return { text: 'Unavailable for selected dates', color: 'text-red-400' };
    }
    
    return { 
      text: `Only ${avail.available_qty} available for selected dates`, 
      color: avail.available_qty <= 2 ? 'text-amber-400' : 'text-slate-400'
    };
  };
  
  // Cart functions
  const addToCart = (product) => {
    // Check if schedule is complete first
    if (!isScheduleComplete) {
      toast.error('Please select pickup and return dates/times first');
      return;
    }
    
    // Check availability before adding
    if (!canAddToCart(product)) {
      const avail = inventoryAvailability[product.sku];
      // Only show "not available" if it's configured, not unlimited, and has 0 qty
      if (avail && avail.is_configured && !avail.unlimited && avail.available_qty === 0) {
        toast.error(`${product.name} is not available for the selected dates`);
      } else {
        toast.error(`Maximum available quantity reached for ${product.name}`);
      }
      return;
    }
    
    const existing = cart.find(item => item.sku === product.sku);
    if (existing) {
      setCart(cart.map(item => 
        item.sku === product.sku 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
      toast.success(`Added ${product.name} to cart`);
    } else {
      // For eCards, add with minimum order quantity
      const minOrder = product.min_order || 1;
      const initialQty = product.type === 'ecard' ? minOrder : 1;
      setCart([...cart, { ...product, quantity: initialQty }]);
      
      if (product.type === 'ecard' && minOrder > 1) {
        toast.success(`Added ${product.name} to cart (minimum order: ${minOrder})`);
      } else {
        toast.success(`Added ${product.name} to cart`);
      }
    }
  };
  
  const updateQuantity = (sku, delta) => {
    setCart(cart.map(item => {
      if (item.sku === sku) {
        const newQty = item.quantity + delta;
        
        // For eCards, enforce minimum order quantity
        if (item.type === 'ecard') {
          const minOrder = item.min_order || 1;
          if (newQty < minOrder) {
            toast.error(`Minimum order for ${item.name} is ${minOrder}`);
            return item;
          }
        } else if (newQty <= 0) {
          return item;
        }
        
        // Check max available for rental items
        if (item.type === 'rental' && delta > 0) {
          const maxAvail = getMaxAvailable(item);
          if (maxAvail !== null && newQty > maxAvail) {
            toast.error(`Maximum available quantity is ${maxAvail}`);
            return item;
          }
        }
        
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  
  const removeFromCart = (sku) => {
    setCart(cart.filter(item => item.sku !== sku));
  };
  
  // Toggle category
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };
  
  // Filtered instructors for search
  const filteredInstructors = useMemo(() => {
    if (!instructorSearch) return eligibleInstructors;
    const search = instructorSearch.toLowerCase();
    return eligibleInstructors.filter(inst => 
      inst.name.toLowerCase().includes(search) ||
      inst.email.toLowerCase().includes(search)
    );
  }, [eligibleInstructors, instructorSearch]);
  
  // Validate form
  const canProceedToCheckout = () => {
    if (!selectedInstructor) return false;
    if (cart.length === 0) return false;
    if (!pickupDate || !returnDate) return false;
    if (!termsAccepted) return false;
    
    // Validate location
    if (pickupLocationType === 'predefined' && !pickupLocationId) return false;
    if (pickupLocationType === 'custom' && (!pickupCustomAddress || !isCustomAddressValid)) return false;
    
    // Check calendar availability - block checkout if pickup or dropoff time is not available
    if (calendarAvailability.pickup && !calendarAvailability.pickup.available) return false;
    if (calendarAvailability.dropoff && !calendarAvailability.dropoff.available) return false;
    
    return true;
  };
  
  // Start checkout
  const handleStartCheckout = async () => {
    if (!canProceedToCheckout()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Clear old tokens first
    setStripePublishableKey(null);
    setStripePromise(null);
    
    // Show checkout dialog (will show loading state)
    setShowCheckout(true);
    
    // Then fetch tokens
    const tokenSuccess = await fetchStripeToken();
    if (!tokenSuccess) {
      setShowCheckout(false);
    }
  };
  
  // Create Stripe order for rental
  const createStripeOrder = async () => {
    try {
      const response = await axios.post(`${API}/rentals/payments/create-order`, {
        amount: total,
        description: `Equipment Rental - ${cart.length} item(s)`,
        metadata: {
          type: 'rental',
          instructor_id: selectedInstructor.id
        }
      });
      return response.data.orderId;
    } catch (error) {
      console.error('Error creating Stripe order:', error);
      throw error;
    }
  };
  
  // Handle payment approval - for Stripe PaymentIntent flow
  const handlePaymentApproval = async ({ paymentIntentId, status }) => {
    setProcessing(true);
    
    // Debug log: show all values that make up the total
    console.log('💳 Rental Payment Approval - Total breakdown:', {
      subtotal: subtotal,
      depositAmount: depositAmount,
      deliveryFee: deliveryFee,
      discountAmount: discountAmount,
      calculatedTotal: subtotal + depositAmount + deliveryFee - discountAmount,
      actualTotal: total,
      paymentIntentId: paymentIntentId
    });
    
    try {
      // Payment is already captured by Stripe when confirmCardPayment succeeds
      // We just need to create the rental order
      
      console.log('✅ Stripe payment verified:', {
        paymentIntentId: paymentIntentId,
        status: status
      });
      
      // Get pickup location details
      const pickupLocation = pickupLocationType === 'predefined'
        ? {
            type: 'predefined',
            predefined_id: pickupLocationId,
            predefined_name: settings.pickup_locations.find(l => l.id === pickupLocationId)?.name
          }
        : { type: 'custom', custom_address: pickupCustomAddress };
      
      // Dropoff location is same as pickup location
      const dropoffLocation = pickupLocation;
      
      // Create rental order with Stripe payment info
      const orderRes = await axios.post(`${API}/rentals/orders`, {
        customer: {
          instructor_id: selectedInstructor.id,
          name: selectedInstructor.name,
          email: selectedInstructor.email,
          phone: selectedInstructor.phone,
          instructor_number: selectedInstructor.instructor_cards?.[0]?.number || ''
        },
        items: cart.map(item => ({
          sku: item.sku,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.price,
          days: (item.type === 'rental' && !item.flat_rate) ? rentalDays : 1,
          type: item.type,
          flat_rate: item.flat_rate || false
        })),
        pickup_location: pickupLocation,
        dropoff_location: dropoffLocation,
        pickup_datetime: `${pickupDate}T${pickupTime}`,
        return_datetime: `${returnDate}T${returnTime}`,
        deposit_amount: depositAmount,
        delivery_fee: deliveryFee,
        subtotal: subtotal,
        total: total,
        terms_accepted: true,
        notes: notes || '',
        coupon: couponApplied ? {
          code: couponApplied.code,
          discount_type: couponApplied.discount_type,
          discount_value: couponApplied.discount_value,
          discount_amount: couponApplied.discount_amount
        } : null,
        payment_method: 'card'
      });
      
      console.log('✅ Rental order created:', orderRes.data);
      
      // Record the Stripe payment info on the order
      try {
        await axios.post(`${API}/rentals/orders/${orderRes.data.order_id}/payment`, {
          transaction_id: paymentIntentId,
          payment_method: 'stripe',
          amount: total
        });
        console.log('✅ Payment recorded for order:', orderRes.data.order_id);
      } catch (paymentError) {
        console.warn('Failed to record payment info (order still created):', paymentError);
      }
      
      // Success
      toast.success('Rental order placed successfully!');
      setCompletedOrderId(orderRes.data.order_id);
      setOrderComplete(true);
      setShowCheckout(false);
      
      // Clear cart
      setCart([]);
      setCouponApplied(null);
      setCouponCode('');
      
    } catch (error) {
      console.error('Order creation error:', error);
      
      // Payment went through but order creation failed
      let errorMessage = 'Failed to create rental order';
      
      // Handle different error formats
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Pydantic validation errors return an array of objects
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      // Don't clear cart so user can retry
    } finally {
      setProcessing(false);
    }
  };
  // Handle payment error from Stripe hosted fields
  const handlePaymentError = (error) => {
    console.error('Stripe payment error:', error);
    let errorMessage = 'Payment failed. Please try again.';
    
    // Parse Stripe hosted fields errors
    if (error.message) {
      const msg = error.message.toLowerCase();
      if (msg.includes('declined') || msg.includes('instrument_declined')) {
        errorMessage = 'Your card was declined. Please try a different payment method.';
      } else if (msg.includes('insufficient') || msg.includes('nsf')) {
        errorMessage = 'Insufficient funds. Please try a different payment method.';
      } else if (msg.includes('expired')) {
        errorMessage = 'Your card has expired. Please use a different card.';
      } else if (msg.includes('invalid') && msg.includes('card')) {
        errorMessage = 'Invalid card number. Please check and try again.';
      } else if (msg.includes('cvv') || msg.includes('security code')) {
        errorMessage = 'Invalid security code (CVV). Please check and try again.';
      } else if (msg.includes('refused') || msg.includes('rejected')) {
        errorMessage = 'Transaction refused. Please contact your bank or try a different card.';
      } else {
        errorMessage = error.message;
      }
    }
    
    toast.error(errorMessage, { duration: 6000 });
    setProcessing(false);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-purple-400" />
      </div>
    );
  }
  
  if (orderComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-slate-800/90 border-purple-500/30 p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Order Confirmed!</h1>
          <p className="text-slate-300 mb-4">Your equipment rental order has been placed successfully.</p>
          <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-400">Order ID</p>
            <p className="text-xl font-mono text-purple-400">{completedOrderId}</p>
          </div>
          <p className="text-sm text-slate-400 mb-6">
            A confirmation email has been sent to {selectedInstructor?.email}
          </p>
          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => window.location.href = `/rental-tracking/${completedOrderId}`}
              className="bg-purple-600 hover:bg-purple-500 w-full"
              data-testid="track-order-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              Track Your Order
            </Button>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="back-to-booking-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Book a Class
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/rentals'}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="back-to-rentals-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Rent More
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Add padding bottom on mobile to account for fixed cart bar */}
      <div className="max-w-7xl mx-auto p-4 md:p-6 pb-72 lg:pb-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Equipment Rentals</h1>
          <p className="text-slate-400">Rent training equipment for your CPR classes</p>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.href = '/'}
              className="text-slate-400 hover:text-white"
              data-testid="back-to-booking"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Booking Site
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowReplacementCosts(true)}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
              data-testid="replacement-costs-btn"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Replacement Costs
            </Button>
          </div>
        </div>
        
        {/* Rental Schedule - AT THE TOP */}
        <Card className="bg-slate-800/80 border-green-500/30 p-4 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-green-400" />
            Rental Schedule
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Pickup Date & Time <span className="text-red-400">*</span></Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-slate-700 border-slate-600 text-white flex-1"
                  data-testid="pickup-date"
                />
                <Input
                  type="time"
                  value={pickupTime}
                  onChange={(e) => handlePickupTimeChange(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white w-28"
                  data-testid="pickup-time"
                  placeholder="--:--"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Return Date & Time <span className="text-red-400">*</span></Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                  min={pickupDate || new Date().toISOString().split('T')[0]}
                  className="bg-slate-700 border-slate-600 text-white flex-1"
                  data-testid="return-date"
                />
                <Input
                  type="time"
                  value={returnTime}
                  onChange={(e) => setReturnTime(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white w-28"
                  data-testid="return-time"
                />
              </div>
            </div>
          </div>
          
          {pickupDate && returnDate && (
            <div className="mt-3 p-2 bg-green-900/30 rounded-lg text-center">
              <p className="text-green-300 text-sm">
                Rental Duration: <span className="font-bold">{rentalDays} day{rentalDays > 1 ? 's' : ''}</span>
              </p>
            </div>
          )}
          
          {/* Calendar availability warnings */}
          {checkingCalendar && (
            <div className="mt-3 p-2 bg-blue-900/30 border border-blue-500/30 rounded-lg flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
              <p className="text-blue-300 text-sm">Checking calendar availability...</p>
            </div>
          )}
          
          {!checkingCalendar && calendarAvailability.pickup && !calendarAvailability.pickup.available && (
            <div className="mt-3 p-3 bg-red-900/40 border border-red-500/50 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <p className="text-red-300 text-sm">Pickup time not available. Please select a different time.</p>
              </div>
            </div>
          )}
          
          {!checkingCalendar && calendarAvailability.dropoff && !calendarAvailability.dropoff.available && (
            <div className="mt-3 p-3 bg-red-900/40 border border-red-500/50 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                <p className="text-red-300 text-sm">Return time not available. Please select a different time.</p>
              </div>
            </div>
          )}
          
          {!isScheduleComplete ? (
            <div className="mt-3 p-2 bg-amber-900/30 border border-amber-500/30 rounded-lg text-center">
              <p className="text-amber-300 text-sm">
                Please complete all pickup and return dates/times before adding items
              </p>
            </div>
          ) : null}
        </Card>
        
        <div className="flex gap-6">
          {/* Main Content Column - Scrollable */}
          <div className="flex-1 space-y-4">
            {/* 1. Rental Schedule - Already at top */}
            
            {/* 2. Pickup & Return Locations */}
            <Card className="bg-slate-800/80 border-orange-500/30 p-4">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-orange-400" />
                Pickup & Return Locations
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pickup Location */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Pickup Location</Label>
                  <Select value={pickupLocationType} onValueChange={(value) => {
                    setPickupLocationType(value);
                    // Reset custom address validation when switching
                    if (value === 'predefined') {
                      setPickupCustomAddress('');
                      setCustomAddressData(null);
                      setIsCustomAddressValid(false);
                    }
                  }}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="predefined">Select from list</SelectItem>
                      <SelectItem value="custom">Enter custom address</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {pickupLocationType === 'predefined' ? (
                    <Select value={pickupLocationId} onValueChange={setPickupLocationId}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {settings?.pickup_locations?.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name} - {loc.address} {loc.delivery_fee > 0 ? `(+$${loc.delivery_fee.toFixed(2)} delivery)` : '(Free delivery)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="space-y-2">
                      <AddressAutocomplete
                        placeholder="Start typing your address..."
                        initialValue={pickupCustomAddress}
                        onAddressSelect={(addressData) => {
                          if (addressData) {
                            const fullAddress = `${addressData.street}, ${addressData.city}, ${addressData.state} ${addressData.zip}`;
                            setPickupCustomAddress(fullAddress);
                            setCustomAddressData(addressData);
                            setIsCustomAddressValid(true);
                          } else {
                            setPickupCustomAddress('');
                            setCustomAddressData(null);
                            setIsCustomAddressValid(false);
                          }
                        }}
                        className="bg-slate-700 border-slate-600 text-white"
                        testId="rental-custom-address"
                        error={pickupLocationType === 'custom' && pickupCustomAddress && !isCustomAddressValid ? 'Please select a valid address from suggestions' : ''}
                      />
                      <p className="text-sm text-purple-400">
                        {settings?.delivery_fee > 0 
                          ? `Delivery Fee: +$${settings.delivery_fee.toFixed(2)}`
                          : 'Free delivery'}
                      </p>
                    </div>
                  )}
                  <p className="text-slate-400 text-xs mt-1">Equipment will be returned to the same location</p>
                </div>
              </div>
            </Card>
            
            {/* 3. Instructor Selection */}
            <Card className="bg-slate-800/80 border-blue-500/30 p-4">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-blue-400" />
                Select Instructor
              </h3>
              
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search instructors..."
                    value={instructorSearch}
                    onChange={(e) => setInstructorSearch(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                
                {selectedInstructor ? (
                  <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{selectedInstructor.name}</p>
                        <p className="text-sm text-slate-400">{selectedInstructor.phone}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedInstructor(null)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <ScrollArea className="h-32 border border-slate-600 rounded-lg">
                    {filteredInstructors.length === 0 ? (
                      <div className="p-4 text-center text-slate-400">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No eligible instructors found</p>
                        <p className="text-xs">Must have a valid instructor card</p>
                      </div>
                    ) : (
                      <div className="p-2 space-y-1">
                        {filteredInstructors.map(instructor => (
                          <div
                            key={instructor.id}
                            onClick={() => setSelectedInstructor(instructor)}
                            className="p-2 rounded hover:bg-slate-700 cursor-pointer transition-colors"
                          >
                            <p className="text-white font-medium text-sm">{instructor.name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                )}
              </div>
            </Card>
            
            {/* 4. Rental Equipment - Always Visible (excludes flat-rate items) */}
            <Card className="bg-slate-800/80 border-purple-500/30">
              <div className="p-4 border-b border-slate-700/50">
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-purple-400" />
                  <h2 className="text-lg font-semibold text-white">Rental Equipment</h2>
                  <span className="text-sm text-slate-400">({products.rentals.filter(p => !p.flat_rate).length})</span>
                </div>
              </div>
              
              {/* Manikin Lungs Notice */}
              <div className="mx-4 mt-4 p-3 bg-amber-900/30 border border-amber-600/40 rounded-lg">
                <div className="flex gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-amber-200 font-medium">Manikin Rental Notice</p>
                    <p className="text-amber-100/80 mt-1">
                      All manikin rentals come with one set of lungs. Lungs must be changed per class and cannot be used for multiple classes per AHA standards. If you need additional lungs, please add a note for how many you need. Additional charge may apply.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {products.rentals.filter(p => !p.flat_rate).length === 0 ? (
                  <div className="col-span-2 text-center py-8 text-slate-400">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No rental items available</p>
                  </div>
                ) : (
                  products.rentals.filter(p => !p.flat_rate).map(product => {
                    const availInfo = getAvailabilityText(product);
                    const maxAvail = getMaxAvailable(product);
                    const avail = inventoryAvailability[product.sku];
                    // Item is unavailable if: configured AND not unlimited AND available_qty is 0
                    const isUnavailable = avail?.is_configured && !avail?.unlimited && avail?.available_qty === 0;
                    const cartItem = cart.find(item => item.sku === product.sku);
                    const currentQty = cartItem ? cartItem.quantity : 0;
                    const canIncrease = isScheduleComplete && (maxAvail === null || currentQty < maxAvail);
                    
                    return (
                      <div 
                        key={product.sku}
                        className={`bg-slate-700/50 rounded-lg p-3 transition-colors ${!isScheduleComplete || isUnavailable ? 'opacity-60' : 'hover:bg-slate-700'}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            {productImages[product.sku] ? (
                              <img 
                                src={productImages[product.sku]} 
                                alt={product.name}
                                className="w-14 h-14 object-cover rounded-lg border border-slate-600 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setLightboxImage({ url: productImages[product.sku], name: product.name })}
                              />
                            ) : loadingImages[product.sku] ? (
                              <div className="w-14 h-14 rounded-lg bg-slate-800/60 border border-slate-600 flex items-center justify-center">
                                <Loader2 className="h-5 w-5 text-slate-500 animate-spin" />
                              </div>
                            ) : (
                              <div className="w-14 h-14 rounded-lg bg-slate-800/60 border border-slate-600 flex items-center justify-center">
                                <Package className="h-6 w-6 text-slate-500" />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm truncate">
                              {product.name}
                            </p>
                            <p className="text-purple-400 text-sm">
                              ${product.price.toFixed(2)}/day
                            </p>
                            {availInfo && (
                              <p className={`text-xs mt-1 ${availInfo.color}`}>
                                {availInfo.text}
                              </p>
                            )}
                          </div>
                          
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-1 bg-slate-800/80 rounded-lg p-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateQuantity(product.sku, -1)}
                              disabled={currentQty === 0}
                              className="h-7 w-7 p-0 text-white hover:bg-slate-600 disabled:opacity-30"
                              data-testid={`minus-${product.sku}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center text-white text-sm font-medium">
                              {currentQty}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => addToCart(product)}
                              disabled={!canIncrease || isUnavailable || loadingAvailability}
                              className={`h-7 w-7 p-0 hover:bg-slate-600 disabled:opacity-30 ${!isScheduleComplete || isUnavailable ? 'text-slate-500' : 'text-white'}`}
                              data-testid={`plus-${product.sku}`}
                            >
                              {loadingAvailability ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Plus className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
            
            {/* 5. Instructor eCards - Always Visible */}
            <Card className="bg-slate-800/80 border-teal-500/30">
              <div className="p-4 border-b border-slate-700/50">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-teal-400" />
                  <h2 className="text-lg font-semibold text-white">Instructor eCards</h2>
                  <span className="text-sm text-slate-400">({products.ecards.length})</span>
                </div>
              </div>
              
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {products.ecards.length === 0 ? (
                  <div className="col-span-2 text-center py-8 text-slate-400">
                    <CreditCard className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No eCards available</p>
                  </div>
                ) : (
                  products.ecards.map(product => {
                    const cartItem = cart.find(item => item.sku === product.sku);
                    const currentQty = cartItem ? cartItem.quantity : 0;
                    const minOrder = product.min_order || 1;
                    
                    return (
                      <div 
                        key={product.sku}
                        className={`bg-slate-700/50 rounded-lg p-3 transition-colors ${!isScheduleComplete ? 'opacity-60' : 'hover:bg-slate-700'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm truncate">{product.name}</p>
                            <p className="text-teal-400 text-sm">
                              ${product.price.toFixed(2)}
                              {minOrder > 1 && (
                                <span className="text-slate-400 text-xs ml-2">(min: {minOrder})</span>
                              )}
                            </p>
                          </div>
                          
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-1 bg-slate-800/80 rounded-lg p-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateQuantity(product.sku, -1)}
                              disabled={currentQty === 0 || currentQty <= minOrder}
                              className="h-7 w-7 p-0 text-white hover:bg-slate-600 disabled:opacity-30"
                              data-testid={`minus-${product.sku}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center text-white text-sm font-medium">
                              {currentQty}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => addToCart(product)}
                              disabled={!isScheduleComplete}
                              className={`h-7 w-7 p-0 hover:bg-slate-600 disabled:opacity-30 ${!isScheduleComplete ? 'text-slate-500' : 'text-white'}`}
                              data-testid={`plus-${product.sku}`}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
            
            {/* 6. One-Time Items (Flat Rate) - Always Visible */}
            {products.rentals.filter(p => p.flat_rate).length > 0 && (
              <Card className="bg-slate-800/80 border-green-500/30">
                <div className="p-4 border-b border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <Package className="h-5 w-5 text-green-400" />
                    <h2 className="text-lg font-semibold text-white">One-Time Items</h2>
                    <span className="text-sm text-slate-400">({products.rentals.filter(p => p.flat_rate).length})</span>
                    <span className="text-xs bg-green-600/40 text-green-300 px-2 py-0.5 rounded">Flat Rate</span>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">These items are charged once regardless of rental duration</p>
                </div>
                
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {products.rentals.filter(p => p.flat_rate).map(product => {
                    const availInfo = getAvailabilityText(product);
                    const maxAvail = getMaxAvailable(product);
                    const avail = inventoryAvailability[product.sku];
                    const isUnavailable = avail?.is_configured && !avail?.unlimited && avail?.available_qty === 0;
                    const cartItem = cart.find(item => item.sku === product.sku);
                    const currentQty = cartItem ? cartItem.quantity : 0;
                    const canIncrease = isScheduleComplete && (maxAvail === null || currentQty < maxAvail);
                    
                    return (
                      <div 
                        key={product.sku}
                        className={`bg-slate-700/50 rounded-lg p-3 transition-colors ${!isScheduleComplete || isUnavailable ? 'opacity-60' : 'hover:bg-slate-700'}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            {productImages[product.sku] ? (
                              <img 
                                src={productImages[product.sku]} 
                                alt={product.name}
                                className="w-14 h-14 object-cover rounded-lg border border-slate-600 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setLightboxImage({ url: productImages[product.sku], name: product.name })}
                              />
                            ) : loadingImages[product.sku] ? (
                              <div className="w-14 h-14 rounded-lg bg-slate-800/60 border border-slate-600 flex items-center justify-center">
                                <Loader2 className="h-5 w-5 text-slate-500 animate-spin" />
                              </div>
                            ) : (
                              <div className="w-14 h-14 rounded-lg bg-slate-800/60 border border-slate-600 flex items-center justify-center">
                                <Package className="h-6 w-6 text-slate-500" />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm truncate">
                              {product.name}
                            </p>
                            <p className="text-green-400 text-sm">
                              ${product.price.toFixed(2)} (one-time)
                            </p>
                            {availInfo && (
                              <p className={`text-xs mt-1 ${availInfo.color}`}>
                                {availInfo.text}
                              </p>
                            )}
                          </div>
                          
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-1 bg-slate-800/80 rounded-lg p-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateQuantity(product.sku, -1)}
                              disabled={currentQty === 0}
                              className="h-7 w-7 p-0 text-white hover:bg-slate-600 disabled:opacity-30"
                              data-testid={`minus-${product.sku}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center text-white text-sm font-medium">
                              {currentQty}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => addToCart(product)}
                              disabled={!canIncrease || isUnavailable || loadingAvailability}
                              className={`h-7 w-7 p-0 hover:bg-slate-600 disabled:opacity-30 ${!isScheduleComplete || isUnavailable ? 'text-slate-500' : 'text-white'}`}
                              data-testid={`plus-${product.sku}`}
                            >
                              {loadingAvailability ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Plus className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
            
            {/* 7. Discount Code */}
            <Card className="bg-slate-800/80 border-amber-500/30 p-4">
              <Label className="text-slate-300 flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4 text-amber-400" />
                Discount Code
              </Label>
              
              {couponApplied ? (
                <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-300 font-medium flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Coupon Applied: {couponApplied.code.toUpperCase()}
                      </p>
                      <p className="text-green-400 text-sm">
                        You save ${couponApplied.discount_amount.toFixed(2)}
                        {couponApplied.discount_type === 'percent' && ` (${couponApplied.discount_value}% off)`}
                      </p>
                      {couponApplied.description && (
                        <p className="text-slate-400 text-xs mt-1">{couponApplied.description}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeCoupon}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError('');
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                      className="bg-slate-700 border-slate-600 text-white uppercase"
                      data-testid="rental-coupon-input"
                    />
                    <Button
                      onClick={applyCoupon}
                      disabled={couponLoading || !couponCode.trim() || cart.length === 0}
                      className="bg-amber-600 hover:bg-amber-500 disabled:opacity-50"
                      data-testid="rental-apply-coupon"
                    >
                      {couponLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  </div>
                  {couponError && (
                    <p className="text-red-400 text-sm flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {couponError}
                    </p>
                  )}
                </div>
              )}
            </Card>
            
            {/* 8. Notes */}
            <Card className="bg-slate-800/80 border-slate-600/30 p-4">
              <Label className="text-slate-300 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-400" />
                Additional Notes (Optional)
              </Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special requests or notes..."
                className="mt-2 w-full h-20 bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-slate-400 resize-none"
              />
            </Card>
          </div>
          
          {/* Sticky Cart Column */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-4">
              <Card className="bg-slate-800/80 border-purple-500/30">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-purple-400" />
                  Your Cart
                  {cart.length > 0 && (
                    <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">
                      {cart.reduce((sum, item) => sum + item.quantity, 0)}
                    </span>
                  )}
                </h3>
              </div>
              
              <ScrollArea className="h-[300px]">
                {cart.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>Your cart is empty</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {cart.map(item => {
                      // Flat rate items charge once, regular rental items charge per day
                      const days = (item.type === 'rental' && !item.flat_rate) ? rentalDays : 1;
                      const itemTotal = item.price * item.quantity * days;
                      const maxAvail = getMaxAvailable(item);
                      const atMax = maxAvail !== null && item.quantity >= maxAvail;
                      
                      return (
                        <div key={item.sku} className={`rounded-lg p-3 ${item.type === 'rental' ? 'bg-purple-900/30 border border-purple-700/50' : 'bg-teal-900/30 border border-teal-700/50'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <p className={`font-medium text-sm ${item.type === 'rental' ? 'text-purple-200' : 'text-teal-200'}`}>
                                {item.name}
                                {item.flat_rate && (
                                  <span className="ml-2 text-xs bg-green-600/40 text-green-300 px-1.5 py-0.5 rounded">Flat Rate</span>
                                )}
                              </p>
                              <p className={`text-xs ${item.type === 'rental' ? 'text-purple-400' : 'text-teal-400'}`}>
                                ${item.price.toFixed(2)}{item.type === 'rental' && !item.flat_rate ? '/day' : ' (one-time)'}
                              </p>
                              {item.type === 'rental' && maxAvail !== null && (
                                <p className={`text-xs mt-0.5 ${atMax ? 'text-amber-400' : 'text-slate-500'}`}>
                                  {atMax ? `Max ${maxAvail} for dates` : `${maxAvail} available`}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFromCart(item.sku)}
                              className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateQuantity(item.sku, -1)}
                                className="h-6 w-6 p-0 text-white"
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-6 text-center text-white text-sm">{item.quantity}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateQuantity(item.sku, 1)}
                                disabled={atMax}
                                className={`h-6 w-6 p-0 ${atMax ? 'text-slate-500 cursor-not-allowed' : 'text-white'}`}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                              {item.type === 'rental' && days > 1 && (
                                <span className="ml-2 text-xs text-purple-400">× {days} days</span>
                              )}
                            </div>
                            <p className={`font-medium ${item.type === 'rental' ? 'text-purple-400' : 'text-teal-400'}`}>
                              ${itemTotal.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
              
              {/* Totals */}
              <div className="p-4 border-t border-slate-700 space-y-2">
                <div className="flex justify-between text-slate-300">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Refundable Deposit</span>
                  <span>${depositAmount.toFixed(2)}</span>
                </div>
                {deliveryFee > 0 && (
                  <div className="flex justify-between text-slate-300">
                    <span>Delivery Fee</span>
                    <span>${deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>Discount</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-white pt-2 border-t border-slate-600">
                  <span>Total</span>
                  <span className="text-purple-400">${total.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Terms */}
              <div className="px-4 pb-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="mt-1"
                  />
                  <label htmlFor="terms" className="text-sm text-slate-300">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-purple-400 hover:text-purple-300 underline"
                    >
                      Terms and Conditions
                    </button>
                  </label>
                </div>
              </div>
              
              {/* Checkout Button */}
              <div className="p-4 pt-2">
                <Button
                  onClick={handleStartCheckout}
                  disabled={!canProceedToCheckout()}
                  className="w-full h-12 bg-purple-600 hover:bg-purple-500 disabled:opacity-50"
                  data-testid="proceed-checkout"
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  Proceed to Checkout
                </Button>
              </div>
            </Card>
            </div>
          </div>
          
          {/* Mobile Cart - Shows at bottom on small screens */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-800 border-t border-purple-500/30 p-4 z-40 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2 mb-3">
              {/* Cart Items */}
              {cart.length > 0 ? (
                <div className="space-y-1 pb-2 border-b border-slate-700">
                  {cart.map((item) => {
                    const days = (item.type === 'rental' && !item.flat_rate) ? rentalDays : 1;
                    return (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-slate-300 truncate max-w-[200px]">
                          {item.name} x{item.quantity}
                          {item.flat_rate && <span className="text-green-400 text-xs ml-1">(flat)</span>}
                        </span>
                        <span className="text-slate-300">${(item.price * item.quantity * days).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-slate-400 pb-2 border-b border-slate-700">
                  <ShoppingCart className="h-4 w-4" />
                  <span>No items in cart</span>
                </div>
              )}
              
              {/* Subtotal */}
              <div className="flex justify-between text-sm text-slate-300">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              
              {/* Deposit */}
              <div className="flex justify-between text-sm text-slate-400">
                <span>Refundable Deposit</span>
                <span>${depositAmount.toFixed(2)}</span>
              </div>
              
              {/* Delivery Fee */}
              {deliveryFee > 0 && (
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Delivery Fee</span>
                  <span>${deliveryFee.toFixed(2)}</span>
                </div>
              )}
              
              {/* Discount */}
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-400">
                  <span>Discount</span>
                  <span>-${discountAmount.toFixed(2)}</span>
                </div>
              )}
              
              {/* Total */}
              <div className="flex justify-between text-white font-bold pt-2 border-t border-slate-600">
                <span>Total</span>
                <span className="text-purple-400">${total.toFixed(2)}</span>
              </div>
            </div>
            <Button
              onClick={handleStartCheckout}
              disabled={!canProceedToCheckout()}
              className="w-full h-10 bg-purple-600 hover:bg-purple-500 disabled:opacity-50"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Checkout
            </Button>
          </div>
        </div>
      </div>
      
      {/* Terms and Conditions Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Terms and Conditions
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh]">
            <div className="text-slate-300 whitespace-pre-wrap p-4">
              {settings?.terms_and_conditions || 'Loading...'}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => setShowTerms(false)} className="bg-purple-600 hover:bg-purple-500">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={(open) => {
        setShowCheckout(open);
        if (!open) {
          // Clear tokens when closing to ensure fresh fetch on next open
          setStripePublishableKey(null);
          setStripePromise(null);
        }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Secure Checkout
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Complete your payment to place your rental order
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Order Summary */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-300 mb-2">Order Summary</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>{cart.length} item(s)</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Refundable Deposit</span>
                  <span>${depositAmount.toFixed(2)}</span>
                </div>
                {deliveryFee > 0 && (
                  <div className="flex justify-between text-slate-300">
                    <span>Delivery Fee</span>
                    <span>${deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>Discount ({couponApplied?.code?.toUpperCase()})</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-white pt-2 border-t border-slate-600">
                  <span>Total</span>
                  <span className="text-purple-400">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* Stripe Elements */}
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <div className="space-y-3">
                  {/* Card Number */}
                  <div>
                    <Label className="text-slate-300 text-sm flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-slate-400" />
                      Card Number
                    </Label>
                    <div className={`mt-1 bg-slate-700 border rounded-lg p-3 ${
                      rentalCardFieldStatus.cardNumber.error 
                        ? 'border-red-500' 
                        : rentalCardFieldStatus.cardNumber.complete 
                          ? 'border-green-500' 
                          : 'border-slate-600'
                    }`}>
                      <CardNumberElement 
                        options={ELEMENT_OPTIONS}
                        onChange={(e) => setRentalCardFieldStatus(prev => ({
                          ...prev,
                          cardNumber: { complete: e.complete, error: e.error?.message || null }
                        }))}
                      />
                    </div>
                  </div>
                  
                  {/* Expiry and CVC Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300 text-sm flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        Expiry
                      </Label>
                      <div className={`mt-1 bg-slate-700 border rounded-lg p-3 ${
                        rentalCardFieldStatus.cardExpiry.error 
                          ? 'border-red-500' 
                          : rentalCardFieldStatus.cardExpiry.complete 
                            ? 'border-green-500' 
                            : 'border-slate-600'
                      }`}>
                        <CardExpiryElement 
                          options={ELEMENT_OPTIONS}
                          onChange={(e) => setRentalCardFieldStatus(prev => ({
                            ...prev,
                            cardExpiry: { complete: e.complete, error: e.error?.message || null }
                          }))}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-sm flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-slate-400" />
                        CVC
                      </Label>
                      <div className={`mt-1 bg-slate-700 border rounded-lg p-3 ${
                        rentalCardFieldStatus.cardCvc.error 
                          ? 'border-red-500' 
                          : rentalCardFieldStatus.cardCvc.complete 
                            ? 'border-green-500' 
                            : 'border-slate-600'
                      }`}>
                        <CardCvcElement 
                          options={ELEMENT_OPTIONS}
                          onChange={(e) => setRentalCardFieldStatus(prev => ({
                            ...prev,
                            cardCvc: { complete: e.complete, error: e.error?.message || null }
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* ZIP Code */}
                  <div>
                    <Label className="text-slate-300 text-sm">ZIP Code</Label>
                    <Input
                      type="text"
                      placeholder="12345"
                      value={rentalPostalCode}
                      onChange={(e) => setRentalPostalCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))}
                      className={`mt-1 bg-slate-700 border text-white placeholder:text-slate-500 ${
                        rentalPostalCode.length >= 5 ? 'border-green-500' : 'border-slate-600'
                      }`}
                    />
                  </div>
                  
                  <RentalStripeSubmitButton
                    onApprove={handlePaymentApproval}
                    onError={handlePaymentError}
                    disabled={processing || !rentalCardFieldStatus.cardNumber.complete || !rentalCardFieldStatus.cardExpiry.complete || !rentalCardFieldStatus.cardCvc.complete || rentalPostalCode.length < 5}
                    total={total}
                    customerEmail={selectedInstructor?.email}
                    customerName={selectedInstructor?.name}
                    description={`Equipment Rental - ${cart.length} items`}
                    postalCode={rentalPostalCode}
                  />
                </div>
              </Elements>
            ) : (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-400" />
                <p className="text-slate-400 mt-2">Loading payment form...</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Replacement Costs Modal */}
      <Dialog open={showReplacementCosts} onOpenChange={setShowReplacementCosts}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <DollarSign className="h-5 w-5" />
              Equipment Replacement Costs
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              If equipment is lost or damaged beyond normal wear, these replacement costs will apply.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[50vh] pr-4">
            <div className="space-y-2">
              {products.rentals
                .filter(p => p.replacement_cost > 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(product => (
                  <div 
                    key={product.sku}
                    className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {productImages[product.sku] ? (
                        <img 
                          src={productImages[product.sku]} 
                          alt={product.name}
                          className="w-10 h-10 object-cover rounded border border-slate-600"
                        />
                      ) : loadingImages[product.sku] ? (
                        <div className="w-10 h-10 rounded bg-slate-600 flex items-center justify-center">
                          <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded bg-slate-600 flex items-center justify-center">
                          <Package className="h-5 w-5 text-slate-400" />
                        </div>
                      )}
                      <span className="text-white text-sm">{product.name}</span>
                    </div>
                    <span className="text-amber-400 font-semibold">
                      ${product.replacement_cost.toFixed(2)}
                    </span>
                  </div>
                ))}
              
              {products.rentals.filter(p => p.replacement_cost > 0).length === 0 && (
                <p className="text-slate-400 text-center py-4">
                  No replacement costs have been set for rental items.
                </p>
              )}
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReplacementCosts(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            {/* Close button */}
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>
            
            {/* Image */}
            <img 
              src={lightboxImage.url} 
              alt={lightboxImage.name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Caption */}
            <p className="text-white text-center mt-3 text-lg font-medium">
              {lightboxImage.name}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentRentals;
